import React, { useRef } from 'react';
import { ContactDetailsBox } from '@/components/blocks/account/contactDetails/ContactDetailBox/ContactDetailBox';
import { Box, Button, Grid, Typography } from '@mui/material';
import { TextField } from '@/components/forms/inputs/TextField/TextField';
import { useForm } from 'react-hook-form';
import {
    IAccountContactFormDetails,
    IAllAccountData,
} from '@/types/types/forms/account/account.interface';
import { accountService } from '@/services/api/accountService/accountService';
import { useCompletionStatus } from '@/hooks/general/useCompletionStatus';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';
import { useServerErrors } from '@/hooks/forms/useServerErrors';
import { ACCOUNT_CONTACT_DETAILS_PHRASES, ACCOUNT_PHRASES } from '@/consts/phrases/accountPhrases';
import { accountFormModel } from '@/components/forms/forms/account/accountFormModel';
import { AccountTabs } from '@/types/enums/forms/account/accountTabs';
import { SaveButtonWithCompletionStatus } from '@/components/general/buttons/SaveButton/SaveButtonWithCompletionStatus';
import { WithConfirmationStatus } from '@/components/forms/wrappers/WithConfirmationStatus/WithConfirmationStatus';
import { WithSendCode } from '@/components/forms/wrappers/WithSendCode/WithSendCode';
import { getFormInputError, getGeneralFetchErrorPhraseId } from '@/utils/getFetchErrorMessage';
import { getFetchErrors } from '@/utils/getFetchErrorMessage';
import { WithCompletionStatus } from '@/components/general/buttons/WithCompletionStatus/WithCompletionStatus';
import { GENERAL_PHRASES } from '@/consts/phrases/generalPhrases';
import { LOGIN_PHRASES } from '@/consts/phrases/loginPhrases';
import { shouldVerifyInput } from './accountContactDetailsFormUtils';
import { useFocusInputCursor } from '@/hooks/general/useFocusInputCursor/useFocusInputCursor';
import { sendMobileCode, setMobileCodeError } from '@/utils/forms/sendMobileCode/sendMobileCode';
import { mutate } from 'swr';
import { config } from '@/config';
import { FormGridErrors } from '@/components/forms/alerts/FormGridErrors/FormGridErrors';
import { InfoTooltip } from '@/components/general/InfoTooltip/InfoTooltip';

import ClearIcon from '@mui/icons-material/Clear';

const ACCOUNT_URL = config.api.url.account;

const {
    email,
    emailCode,
    mobilePhone,
    mobilePhoneCode,
    alternativePhone1,
    alternativePhone2,
    SMSOptIn,
} = accountFormModel[AccountTabs.ContactDetails];

interface AccountContactDetailsFormProps {
    accountDetails: IAllAccountData | undefined;
}

export const AccountContactDetailsForm = ({ accountDetails }: AccountContactDetailsFormProps) => {
    const { t } = usePhrase();

    const { control, handleSubmit, formState, getValues, watch, setValue, setError, clearErrors } =
        useForm<Partial<IAccountContactFormDetails>>({
            values: {
                email: accountDetails?.pendingEmail ?? accountDetails?.email,
                mobilePhone: accountDetails?.mobilePhone,
                mobilePhoneCode: undefined,
                mobilePhoneCodeId: undefined,
                alternativePhone1: accountDetails?.alternativePhone1,
                alternativePhone2: accountDetails?.alternativePhone2,
                SMSOptIn: accountDetails?.SMSOptIn,
            },
            context: { isMobilePhoneOptional: accountDetails?.isMobilePhoneOptional },
        });

    const {
        getCompletionStatus: getFormCompletionStatus,
        resetCompletionStatus: resetFormCompletionStatus,
        completionStatus: formCompletionStatus,
    } = useCompletionStatus();
    const {
        getCompletionStatus: getConfirmEmailCompletionStatus,
        resetCompletionStatus: resetConfirmEmailCompletionStatus,
        completionStatus: confirmEmailCompletionStatus,
    } = useCompletionStatus();

    const { handleServerErrors, serverErrors, clearGeneralErrors } = useServerErrors({
        setError,
        clearErrors,
        getValues,
    });
    const { errors, isSubmitting } = formState;

    const shouldVerifyPhone = shouldVerifyInput(
        accountDetails?.mobilePhone,
        watch('mobilePhone'),
        Boolean(accountDetails?.isMobilePhoneConfirmed),
    );

    const shouldVerifyEmail = shouldVerifyInput(
        accountDetails?.email,
        watch('email'),
        Boolean(accountDetails?.isEmailConfirmed),
    );

    const shouldVerifyEmailWithOtp = shouldVerifyEmail && Boolean(accountDetails?.isEmailConfirmed);
    const shouldVerifyEmailWithoutOtp = shouldVerifyEmail && !accountDetails?.isEmailConfirmed;

    const emailInputRef = useRef<HTMLInputElement>(null);
    const phoneInputRef = useRef<HTMLInputElement>(null);
    const { handleInputChange: handlePhoneInputChange } = useFocusInputCursor({
        inputRef: phoneInputRef,
        shouldRefocus: shouldVerifyPhone,
    });
    const { handleInputChange: handleEmailInputChange } = useFocusInputCursor({
        inputRef: emailInputRef,
        shouldRefocus: shouldVerifyEmail,
    });

    const handleDeletePendingEmail = async () => {
        try {
            await accountService.deletePendingEmail();
            setValue('email', accountDetails?.email);
            clearErrors('email');
            await mutate(ACCOUNT_URL);
        } catch (err) {
            setError(
                'email',
                {
                    type: 'custom',
                    message: t(getGeneralFetchErrorPhraseId(err), {
                        replaceTextVars: getFetchErrors(err)?.generalErrors[0].phrasePlaceholders,
                    }) as string,
                },
                { shouldFocus: true },
            );
        }
    };

    const handleSendPendingEmailCode = async (): Promise<boolean> => {
        try {
            const pendingEmail = watch('email');
            if (pendingEmail) {
                await accountService.sendPendingEmail(pendingEmail);
                clearErrors('email');
            }
            return true;
        } catch (err) {
            setError('email', getFormInputError(err, t, 'pendingEmail'), { shouldFocus: true });
            return false;
        }
    };

    const handleSendMobileCode = async (): Promise<boolean> => {
        try {
            const res = await sendMobileCode(getValues('mobilePhone'), accountDetails?.countryCode);
            const { success, error } = res;
            if (success) {
                setValue('mobilePhoneCodeId', success.mobilePhoneCodeId);
                clearErrors('mobilePhone');
            }
            if (error) {
                setMobileCodeError(error, setError, t);
            }
            return !!success;
        } catch (err: unknown) {
            console.error(err);
            return false;
        }
    };

    const handleSendVerifyEmail = async (): Promise<boolean> => {
        try {
            await getConfirmEmailCompletionStatus(() =>
                accountService.sendConfirmationEmail('account'),
            );
            clearErrors('email');
            return true;
        } catch (err) {
            setError(
                'email',
                {
                    type: 'custom',
                    message: t(getGeneralFetchErrorPhraseId(err), {
                        replaceTextVars: getFetchErrors(err)?.generalErrors[0].phrasePlaceholders,
                    }) as string,
                },
                { shouldFocus: true },
            );
            return false;
        }
    };

    const onSubmit = async (vals: Partial<IAccountContactFormDetails>) => {
        try {
            const { mobilePhoneCode, ...otherVals } = vals;
            await getFormCompletionStatus(() =>
                accountService.saveContactDetails({
                    mobilePhoneCode,
                    mobilePhoneCodeId: getValues('mobilePhoneCodeId'),
                    ...otherVals,
                }),
            );
            await mutate(ACCOUNT_URL);
            clearErrors([
                email.name,
                mobilePhone.name,
                alternativePhone1.name,
                alternativePhone2.name,
                SMSOptIn.name,
            ]);
            clearGeneralErrors();
        } catch (err) {
            handleServerErrors(err);
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <FormGridErrors errors={serverErrors} gridSx={{ mb: 2 }} generalErrorId="message_box" />
            <ContactDetailsBox type="email">
                <Grid size={12} sx={{ pl: 1 }}>
                    <WithConfirmationStatus
                        pendingPhrase={
                            shouldVerifyEmailWithOtp
                                ? (t(ACCOUNT_CONTACT_DETAILS_PHRASES.PLEASE_CONFIRM_EMAIL, {
                                      replaceTextVars: [
                                          t(
                                              accountDetails?.pendingEmail
                                                  ? ACCOUNT_CONTACT_DETAILS_PHRASES.RESEND_CONFIRMATION_CODE
                                                  : LOGIN_PHRASES.SEND_CODE,
                                          ) as string,
                                          accountDetails?.email || '',
                                      ],
                                  }) as string)
                                : (t(
                                      ACCOUNT_CONTACT_DETAILS_PHRASES.VERIFICATION_IN_PROGRESS,
                                  ) as string)
                        }
                        confirmed={!shouldVerifyEmail}
                    >
                        {shouldVerifyEmailWithOtp && (
                            <WithSendCode
                                codeType="email"
                                buttonLabel={
                                    t(
                                        accountDetails?.pendingEmail
                                            ? ACCOUNT_CONTACT_DETAILS_PHRASES.RESEND_CONFIRMATION_CODE
                                            : LOGIN_PHRASES.SEND_CODE,
                                    ) as string
                                }
                                buttonBreakpoint={accountDetails?.pendingEmail ? 9 : 7}
                                hasSendCode={shouldVerifyEmailWithOtp}
                                onSendCode={handleSendPendingEmailCode}
                                errorMessage={errors.emailCode?.message || ''}
                                codeFormInput={
                                    <TextField
                                        label={emailCode.label}
                                        focused={accountDetails?.pendingEmail ? true : false}
                                        name={emailCode.name}
                                        control={control}
                                        error={errors.emailCode}
                                        data-testid="emailCode-input"
                                        required={true}
                                        autoFocus={false}
                                        hideError={true}
                                        fullWidth
                                    />
                                }
                            >
                                <TextField
                                    label={email.label}
                                    name={email.name}
                                    control={control}
                                    error={errors.email}
                                    inputRef={emailInputRef}
                                    onChange={handleEmailInputChange}
                                    data-testid="email-input"
                                    fullWidth
                                    endAdornment={
                                        <Box
                                            onClick={handleDeletePendingEmail}
                                            sx={{
                                                color: 'secondary',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <ClearIcon />
                                            {t(GENERAL_PHRASES.CANCEL)}
                                        </Box>
                                    }
                                />
                            </WithSendCode>
                        )}
                        {shouldVerifyEmailWithoutOtp && (
                            <Grid container>
                                <Grid size={12}>
                                    <TextField
                                        label={email.label}
                                        name={email.name}
                                        control={control}
                                        error={errors.email}
                                        onChange={handleEmailInputChange}
                                        inputRef={emailInputRef}
                                        data-testid="email-input"
                                        fullWidth
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <WithCompletionStatus
                                        alignment="flex-start"
                                        status={confirmEmailCompletionStatus}
                                        resetStatus={resetConfirmEmailCompletionStatus}
                                        successMessage={GENERAL_PHRASES.SENT}
                                    >
                                        <Button
                                            variant={'outlined'}
                                            onClick={handleSendVerifyEmail}
                                        >
                                            {t(LOGIN_PHRASES.RESEND_CONFIRMATION_EMAIL)}
                                        </Button>
                                    </WithCompletionStatus>
                                </Grid>
                            </Grid>
                        )}
                        {!shouldVerifyEmail && (
                            <TextField
                                label={email.label}
                                name={email.name}
                                control={control}
                                error={errors.email}
                                onChange={handleEmailInputChange}
                                inputRef={emailInputRef}
                                data-testid="email-input"
                                fullWidth
                            />
                        )}
                    </WithConfirmationStatus>
                </Grid>
            </ContactDetailsBox>
            <ContactDetailsBox type="phone">
                <Typography variant="subtitle2" sx={{ mt: 1, mb: 3, ml: 1, fontWeight: 'bold' }}>
                    {t(ACCOUNT_CONTACT_DETAILS_PHRASES.PRIMARY)}
                </Typography>
                <Grid size={12} sx={{ pl: 1 }}>
                    <WithConfirmationStatus
                        pendingPhrase={
                            t(ACCOUNT_CONTACT_DETAILS_PHRASES.PLEASE_CONFIRM_PHONE) as string
                        }
                        confirmed={Boolean(
                            accountDetails?.isMobilePhoneConfirmed && !shouldVerifyPhone,
                        )}
                    >
                        {shouldVerifyPhone ? (
                            <WithSendCode
                                codeType="phone"
                                hasSendCode={shouldVerifyPhone}
                                onSendCode={handleSendMobileCode}
                                errorMessage={errors.mobilePhoneCode?.message || ''}
                                codeFormInput={
                                    <TextField
                                        name={mobilePhoneCode.name}
                                        label={mobilePhoneCode.label}
                                        data-testid="mobilePhoneCode-input"
                                        control={control}
                                        required={true}
                                        autoFocus={false}
                                        hideError={true}
                                        fullWidth
                                    />
                                }
                            >
                                <TextField
                                    label={mobilePhone.label}
                                    name={mobilePhone.name}
                                    control={control}
                                    inputRef={phoneInputRef}
                                    onChange={handlePhoneInputChange}
                                    error={errors.mobilePhone}
                                    data-testid="mobilePhone-input"
                                    fullWidth
                                    slotProps={{
                                        input: {
                                            endAdornment: (
                                                <InfoTooltip
                                                    title={GENERAL_PHRASES.PHONE_INFO_TOOLTIP}
                                                />
                                            ),
                                        },
                                    }}
                                />
                            </WithSendCode>
                        ) : (
                            <TextField
                                label={mobilePhone.label}
                                name={mobilePhone.name}
                                inputRef={phoneInputRef}
                                onChange={handlePhoneInputChange}
                                control={control}
                                data-testid="mobilePhone-input"
                                error={errors.mobilePhone}
                                fullWidth
                                slotProps={{
                                    input: {
                                        endAdornment: (
                                            <InfoTooltip
                                                title={GENERAL_PHRASES.PHONE_INFO_TOOLTIP}
                                            />
                                        ),
                                    },
                                }}
                            />
                        )}
                    </WithConfirmationStatus>
                </Grid>
                <Typography variant="subtitle2" sx={{ mt: 4, mb: 2.5, ml: 1, fontWeight: 'bold' }}>
                    {t(ACCOUNT_CONTACT_DETAILS_PHRASES.ALTERNATIVE_PHONE)}
                </Typography>
                <Grid size={12} sx={{ pl: 1 }}>
                    <TextField
                        label={alternativePhone1.label}
                        name={alternativePhone1.name}
                        control={control}
                        error={errors.alternativePhone1}
                        fullWidth
                    />
                </Grid>
                <Grid size={12} sx={{ pl: 1, mt: 2 }}>
                    <TextField
                        label={alternativePhone2.label}
                        name={alternativePhone2.name}
                        control={control}
                        error={errors.alternativePhone2}
                        fullWidth
                    />
                </Grid>
            </ContactDetailsBox>
            <Grid
                container
                direction="row"
                sx={{
                    justifyContent: 'flex-end',
                    my: 2,
                }}
            >
                <SaveButtonWithCompletionStatus
                    id="contact-details-submit"
                    label={ACCOUNT_PHRASES.SAVE}
                    isSubmitting={isSubmitting}
                    status={formCompletionStatus}
                    resetStatus={resetFormCompletionStatus}
                />
            </Grid>
        </form>
    );
};
